import { triathlonDistances } from './sportMetrics'
import type { L10n, SportFieldOption, SportId, SportProfileField } from '../types'

/**
 * CENTRAL SPORTS CATALOGUE — categorical / profile fields.
 * Shown only on their own sport, never plotted as numeric charts, private by default.
 * `value` is prototype sample data; real values are user-entered (Marco 3 persistence).
 */

const o = (id: string, pt: string, en: string = pt): SportFieldOption => ({
  id,
  label: { 'pt-BR': pt, 'en-US': en },
})
const l = (pt: string, en: string): L10n => ({ 'pt-BR': pt, 'en-US': en })

/** Shared by Boxing and Muay Thai. */
export const weightCategories: SportFieldOption[] = [
  o('flyweight', 'Mosca', 'Flyweight'),
  o('bantamweight', 'Galo', 'Bantamweight'),
  o('featherweight', 'Pena', 'Featherweight'),
  o('lightweight', 'Leve', 'Lightweight'),
  o('welterweight', 'Meio-médio', 'Welterweight'),
  o('middleweight', 'Médio', 'Middleweight'),
  o('lightHeavyweight', 'Meio-pesado', 'Light Heavyweight'),
  o('heavyweight', 'Pesado', 'Heavyweight'),
]

export const sportProfileFields: SportProfileField[] = [
  {
    id: 'triathlon:raceDistance',
    sport: 'triathlon',
    label: l('Distância de prova', 'Race distance'),
    type: 'select',
    options: triathlonDistances,
    value: 'sprint',
  },
  {
    id: 'yoga:style',
    sport: 'yoga',
    label: l('Estilo', 'Style'),
    type: 'select',
    options: [o('hatha', 'Hatha'), o('vinyasa', 'Vinyasa'), o('ashtanga', 'Ashtanga'), o('yin', 'Yin'), o('other', 'Outro', 'Other')],
    value: 'vinyasa',
  },
  {
    id: 'pilates:modality',
    sport: 'pilates',
    label: l('Modalidade', 'Modality'),
    type: 'select',
    options: [o('mat', 'Mat / Solo', 'Mat'), o('reformer', 'Reformer')],
    value: 'reformer',
  },
  {
    id: 'jiujitsu:belt',
    sport: 'jiujitsu',
    label: l('Faixa', 'Belt'),
    type: 'select',
    options: [
      o('white', 'Branca', 'White'),
      o('blue', 'Azul', 'Blue'),
      o('purple', 'Roxa', 'Purple'),
      o('brown', 'Marrom', 'Brown'),
      o('black', 'Preta', 'Black'),
    ],
    value: 'blue',
  },
  {
    id: 'jiujitsu:modality',
    sport: 'jiujitsu',
    label: l('Modalidade', 'Modality'),
    type: 'select',
    options: [o('gi', 'Gi'), o('nogi', 'No-Gi')],
    value: 'gi',
  },
  {
    id: 'jiujitsu:graduationDate',
    sport: 'jiujitsu',
    label: l('Graduação na faixa atual', 'Current-belt graduation'),
    type: 'date',
    value: '2024-03-15',
  },
  {
    id: 'jiujitsu:beltTenure',
    sport: 'jiujitsu',
    label: l('Tempo na faixa atual', 'Time at current belt'),
    type: 'tenure',
    from: 'jiujitsu:graduationDate',
  },
  {
    id: 'muayThai:prajied',
    sport: 'muayThai',
    label: l('Prajied · cor', 'Prajied · colour'),
    type: 'text',
    value: 'Vermelho',
  },
  {
    id: 'muayThai:weightCategory',
    sport: 'muayThai',
    label: l('Categoria de peso', 'Weight category'),
    type: 'select',
    options: weightCategories,
    value: 'lightweight',
  },
  {
    id: 'boxing:weightCategory',
    sport: 'boxing',
    label: l('Categoria de peso', 'Weight category'),
    type: 'select',
    options: weightCategories,
    value: 'welterweight',
  },
  {
    id: 'boxing:title',
    sport: 'boxing',
    label: l('Título / federação', 'Title / federation'),
    type: 'text',
    value: '',
    optional: true,
  },
]

export function profileFieldsForSport(sport: SportId): SportProfileField[] {
  return sportProfileFields.filter((field) => field.sport === sport)
}

/** Sample values keyed by field id (seed for the prototype's in-memory state). */
export const defaultSportFieldValues: Record<string, string> = Object.fromEntries(
  sportProfileFields.flatMap((field) => (field.type === 'tenure' ? [] : [[field.id, field.value]]))
)
